import { Loader2, MoreHorizontal } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@teach/ui'
import type { Lang } from '@/types/lang'
import { LANG_INFO } from '@/features/editor/lib/courseLanguages'
import { LanguageFlag } from '@/components/layout/sidebar/LanguageFlag'
import { useMessages } from '@/i18n'
import { DisabledHint } from '../EditorHeader/translation/DisabledHint'
import { messages } from './messages'

interface Props {
  lang: Lang
  /** Базовый язык: чип вместо действий. */
  isBase?: boolean
  /** Статус перевода под названием. */
  status?: ReactNode
  /** Тумблер видимости справа. */
  control?: ReactNode
  /** Пункты «⋯» погашены: ждём сервер, идёт прогон или проверка курса. */
  disabled?: boolean
  /** Почему погашены пункты «⋯», если причину стоит назвать. */
  disabledHint?: string | null
  /** Ждём сервер: на месте тумблера индикатор загрузки. */
  pending?: boolean
  /** Есть что удалять на сервере; иначе пункта нет. */
  onDelete?: () => void
  onMakeBase?: () => void
  /** Сервер откажет: язык скрыт. Пункт погашен, причина — в подсказке. */
  makeBaseBlocked?: boolean
  children?: ReactNode
}

/** Карточка языка вкладки «Язык» — та же расстановка, что в меню «Перевод» шапки: статус под
 *  названием, справа тумблер и «⋯» с редкими действиями. */
export const LanguageCard = ({
  lang,
  isBase,
  status,
  control,
  disabled = false,
  disabledHint = null,
  pending = false,
  onDelete,
  onMakeBase,
  makeBaseBlocked = false,
  children,
}: Props) => {
  const m = useMessages(messages)
  const info = LANG_INFO[lang]
  return (
    <div className="space-y-3 rounded-2xl bg-muted/50 p-4">
      <div className="flex items-center gap-3">
        <LanguageFlag lang={lang} className="size-8 shrink-0" />
        <span className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-base font-medium">{info.name}</span>
          {status}
        </span>
        {isBase ? (
          <span className="ml-auto rounded-full bg-brand px-2.5 py-1 text-xs font-medium leading-none text-brand-foreground">
            {m.baseBadge}
          </span>
        ) : (
          <span className="ml-auto flex shrink-0 items-center gap-2">
            {pending ? (
              <Loader2 aria-hidden className="size-4 animate-spin text-muted-foreground" />
            ) : null}
            {control}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={m.langActionsAria(info.name)}>
                  <MoreHorizontal aria-hidden className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DisabledHint
                  inset
                  text={disabledHint ?? (makeBaseBlocked ? m.makeBaseNeedsVisible : null)}
                >
                  <DropdownMenuItem disabled={disabled || makeBaseBlocked} onSelect={onMakeBase}>
                    {m.makeBase}
                  </DropdownMenuItem>
                </DisabledHint>
                {onDelete ? (
                  <DisabledHint text={disabledHint} inset>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      disabled={disabled}
                      onSelect={onDelete}
                    >
                      {m.deleteLang}
                    </DropdownMenuItem>
                  </DisabledHint>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
        )}
      </div>
      {children}
    </div>
  )
}
